/*!
 * SS7 collection back-restore (bfcache-independent).
 * ---------------------------------------------------------------------------
 * Why this exists: Shopify serves storefront pages with `Cache-Control:
 * no-store` for any session that has a cart / active session. `no-store` makes
 * the page categorically ineligible for the browser back/forward cache, so
 * pressing Back reloads the collection from scratch — losing every "Load more"
 * product and dropping the shopper deep into a now-much-shorter page ("it cuts
 * off, can't scroll back up"). We cannot change Shopify's response headers, so
 * we restore the loaded grid + scroll position ourselves, with no visible flash.
 *
 * Design notes:
 *  - Only runs on /collections/ URLs.
 *  - `history.scrollRestoration = 'manual'` so the browser never dumps the user
 *    into a shrunken page; we own the scroll position.
 *  - Snapshot (grid innerHTML + scrollY + item count) saved on pagehide,
 *    tab-hide, and shortly after each Load-more append. Stored per collection
 *    path in sessionStorage, 30-min freshness.
 *  - Restore only fires on genuine Back/Forward navigations (not fresh visits).
 *  - After re-injecting the grid we re-kick the theme lazyloader
 *    (lazySizesT4.loader.checkElems) so images that were saved in an unloaded
 *    state actually load.
 *  - The grid is pre-hidden (opacity:0) by an inline head snippet ONLY on the
 *    restore path; we reveal it the same frame we set scroll, so there is no
 *    top-of-page flash and no white mask. A CSS keyframe force-reveals after
 *    2.5s as a safety net if this script ever fails to run.
 *  - Fill-in fallback (24 Sep 2026): Load more pushes ?page=N into the URL, so
 *    whenever the snapshot can't be used (older than 30 min, page refreshed,
 *    tab reopened) the shopper landed on a bare ?page=N showing ONLY that page's
 *    products, with no way back up to the earlier ones. Now any ?page=N view
 *    that wasn't restored fetches pages 1..N-1 fresh and prepends them, so the
 *    full list is always there. Skipped for crawlers so the indexed ?page=N
 *    URLs keep their own content.
 */
(function () {
  'use strict';

  if (location.pathname.indexOf('/collections/') === -1) return;

  // Key per collection view INCLUDING filter/sort, but ignoring the `page`
  // param so a load-more'd view (?page=2) maps to the same snapshot as its base.
  // Must match the inline pre-paint snippet in meta-tags.liquid exactly.
  function KEYfn() {
    var q = '';
    try {
      var p = new URLSearchParams(location.search);
      p.delete('page');
      q = p.toString();
    } catch (e) { q = ''; }
    return 'ss7:colr:' + location.pathname + (q ? ('?' + q) : '');
  }
  var KEY = KEYfn();
  var MAX_AGE = 30 * 60 * 1000; // 30 minutes
  var GRID_SEL = '.t4s-products';
  var ITEM_SEL = '.t4s-product';
  var BOT_RE = /bot|crawl|spider|slurp|lighthouse|headless/i;
  var revealTimer;
  var filling = false;

  function grid() { return document.querySelector(GRID_SEL); }

  function itemCount(g) {
    if (!g) return 0;
    var n = 0, c = g.children, i = 0;
    for (; i < c.length; i++) { if (c[i].classList && c[i].classList.contains('t4s-product')) n++; }
    return n;
  }

  function reveal() {
    clearTimeout(revealTimer);
    var h = document.documentElement;
    if (h.classList) h.classList.remove('ss7-col-restoring');
    else h.className = h.className.replace(/(^|\s)ss7-col-restoring(\s|$)/g, ' ');
  }

  // We manage scroll ourselves so returning to this entry never restores into a
  // shrunken page. Set on every collection load so it is in effect on the entry
  // BEFORE the shopper navigates into a product.
  try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}

  /* ----------------------------------------------------------------- SAVE */
  function save() {
    if (filling) return; // never snapshot a half-rebuilt grid
    try {
      var g = grid();
      if (!g) return;
      sessionStorage.setItem(KEY, JSON.stringify({
        html: g.innerHTML,
        y: window.pageYOffset || document.documentElement.scrollTop || 0,
        count: itemCount(g),
        t: Date.now()
      }));
    } catch (e) { /* quota / disabled storage — restore just no-ops */ }
  }

  // pagehide covers normal unloads and entering bfcache; visibilitychange is the
  // reliable fallback on mobile Safari where pagehide can be flaky.
  window.addEventListener('pagehide', save, true);
  window.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') save();
  });
  // Capture the grown grid right after a Load-more append, in case the shopper
  // clicks into a product before any further scroll/hide event fires.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('.t4s-loadmore-btn')) setTimeout(save, 1500);
  }, true);

  // bfcache path: when the cart is empty the collection IS eligible for the browser
  // back/forward cache, so Back restores the whole page (grid + everything) from
  // memory and DOMContentLoaded does NOT fire — restore() below never runs, which is
  // fine because the DOM is already intact. BUT scrollRestoration='manual' suppressed
  // the browser's own scroll restore, so re-apply the saved position here.
  window.addEventListener('pageshow', function (e) {
    if (!e || !e.persisted) return; // fresh loads are handled by restore() on DOMContentLoaded
    reveal();
    try {
      var raw = sessionStorage.getItem(KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (s && typeof s.y === 'number') {
          window.scrollTo(0, s.y);
          setTimeout(function () { window.scrollTo(0, s.y); }, 60);
        }
      }
    } catch (e2) {}
  });

  /* -------------------------------------------------------------- RESTORE */
  function isBackForward() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type === 'back_forward';
    } catch (e) {}
    try { return performance.navigation && performance.navigation.type === 2; } catch (e) {}
    return false;
  }

  /* ---------------------------------------------------- FILL-IN FALLBACK */
  function pageNum() {
    try {
      var n = parseInt(new URLSearchParams(location.search).get('page'), 10);
      return n > 1 ? n : 1;
    } catch (e) { return 1; }
  }

  // Product handle of a card, whether its link is /products/x or the theme's
  // rewritten /collections/c/products/x — used to avoid duplicate cards.
  function cardKey(el) {
    var a = el.querySelector && el.querySelector('a[href*="/products/"]');
    if (!a) return '';
    var h = a.getAttribute('href') || '';
    return h.split('?')[0].replace(/^.*\/products\//, '');
  }

  // Fetch pages 1..N-1 of this exact view (filters + sort kept) and prepend their
  // cards. Only the collection section is requested (section_id) to keep it light,
  // one page at a time. All-or-nothing: a failed page leaves the grid untouched
  // rather than prepend a list with a hole in it. done(true) if cards were added.
  function fillEarlier(g, done) {
    var last = pageNum();
    if (last < 2 || BOT_RE.test(navigator.userAgent || '') ||
        !window.fetch || !window.DOMParser) { done(false); return; }

    var sec = g.closest ? g.closest('.shopify-section') : null;
    var sid = sec && sec.id ? sec.id.replace(/^shopify-section-/, '') : '';
    var cards = [], styles = [], p = 1;
    filling = true;

    function url(n) {
      var u = new URL(location.href);
      if (n === 1) u.searchParams.delete('page'); else u.searchParams.set('page', n);
      if (sid) u.searchParams.set('section_id', sid);
      return u.pathname + u.search;
    }
    function finish(ok) { filling = false; done(ok); }
    function insert() {
      var seen = {}, c = g.children, i, k, added = 0;
      var frag = document.createDocumentFragment();
      for (i = 0; i < c.length; i++) seen[cardKey(c[i])] = 1;
      for (i = 0; i < cards.length; i++) {
        k = cardKey(cards[i]);
        if (k && seen[k]) continue;
        seen[k] = 1;
        frag.appendChild(document.importNode(cards[i], true));
        added++;
      }
      if (added) {
        for (i = 0; i < styles.length; i++) frag.insertBefore(document.importNode(styles[i], true), frag.firstChild);
        g.insertBefore(frag, g.firstChild);
        // Same re-init the theme runs after its own Load more (swatches, hover
        // image, wishlist, collection links), then images, then a resize so the
        // SS7 card chips / hearts / colour counts are applied to the new cards.
        try { if (window.T4SThemeSP && T4SThemeSP.reinitProductGridItem) T4SThemeSP.reinitProductGridItem(); } catch (e) {}
        try {
          if (window.lazySizesT4 && lazySizesT4.loader && lazySizesT4.loader.checkElems) {
            lazySizesT4.loader.checkElems();
          }
        } catch (e) {}
        try { window.dispatchEvent(new Event('resize')); } catch (e) {}
      }
      finish(added > 0);
    }
    function next() {
      if (p >= last) { insert(); return; }
      fetch(url(p), { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (html) {
          var src = new DOMParser().parseFromString(html, 'text/html').querySelector(GRID_SEL);
          if (!src) throw new Error('no grid');
          for (var c = src.children, i = 0; i < c.length; i++) {
            if (c[i].classList && c[i].classList.contains('t4s-product')) cards.push(c[i]);
            // 27 Oct 2026: keep the promo-tile <style> too, or the tiles render unstyled (giant stars).
            else if (c[i].tagName === 'STYLE') styles.push(c[i]);
          }
          p++;
          next();
        })
        .catch(function () { finish(false); });
    }
    next();
  }

  function restore() {
    // Safety: whatever happens below, never leave the grid hidden.
    revealTimer = setTimeout(reveal, 2500);

    var g = grid();
    if (!g) { reveal(); return; }

    var bf = isBackForward();
    var s = null;
    if (bf) {
      try { s = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { s = null; }
    }
    var fresh = !!(s && s.html && (Date.now() - s.t) <= MAX_AGE);

    // Re-inject the fuller grid only if it actually has more than what rendered.
    var injected = false;
    if (fresh && s.count > itemCount(g)) {
      try { g.innerHTML = s.html; injected = true; } catch (e) {}
      try {
        if (window.lazySizesT4 && lazySizesT4.loader && lazySizesT4.loader.checkElems) {
          lazySizesT4.loader.checkElems();
        }
      } catch (e) {}
    }

    // No usable snapshot but we're on ?page=N: rebuild pages 1..N-1 from the
    // store. Grid stays hidden while it fills (the head CSS force-reveals at
    // 2.5s regardless). On Back, return to the saved spot if the snapshot was
    // of a fuller grid than this bare page; otherwise leave the view alone.
    if (!injected && pageNum() > 1) {
      var bare = itemCount(g);
      var h = document.documentElement;
      if (h.classList) h.classList.add('ss7-col-restoring');
      fillEarlier(g, function (ok) {
        if (ok && s && typeof s.y === 'number' && s.count > bare) {
          try {
            var sb = h.style.scrollBehavior;
            h.style.scrollBehavior = 'auto';
            void g.offsetHeight;
            window.scrollTo(0, s.y);
            h.style.scrollBehavior = sb || '';
          } catch (e) {}
        }
        reveal();
      });
      return;
    }

    if (!bf || !fresh) { reveal(); return; }

    var y = s.y || 0;
    var prevBehavior;
    try {
      prevBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
    } catch (e) {}

    function place() { try { window.scrollTo(0, y); } catch (e) {} }

    // Restore scroll + reveal SYNCHRONOUSLY so it works even when the tab is hidden
    // (requestAnimationFrame is paused while a page is not visible — e.g. the shopper
    // opened the product in a background tab, or switched tabs before returning).
    // Force a reflow first so scrollHeight already reflects the injected grid; the
    // product cards use fixed-ratio image boxes, so height is stable before images
    // finish loading and y stays valid. Re-apply across a few frames/timeouts to
    // survive any late reflow as images settle.
    try { if (g) { void g.offsetHeight; } } catch (e) {}
    place();
    reveal();
    try { requestAnimationFrame(function () { place(); requestAnimationFrame(place); }); } catch (e) {}
    setTimeout(place, 120);
    setTimeout(function () {
      place();
      try { document.documentElement.style.scrollBehavior = prevBehavior || ''; } catch (e) {}
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore);
  } else {
    restore();
  }
})();
